import { DossierContextProps } from 'contexts-types';
import useWebSocket from 'hooks/useWebSocket';
import { Columns, FilterProps, Props } from 'props';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useState } from 'react';
import { DossierPdfStatus, DossierStatus } from 'types/enums';
import {
  IAvailableExternalFontViewModel,
  IDossierPreviewViewModel,
  IDossierViewModel,
} from 'viewModels';
import { useAuth } from './auth';
import { useInfiniteQuery } from 'react-query';
import {
  IOrderFieldInputModel,
  IRequestDossierInputModel,
} from 'inputModels';
import { DossierController } from 'controllers';
import { useAlert } from './alert';

const PAGE_SIZE = 10;
const DossierContext = createContext<DossierContextProps>(
  {} as DossierContextProps
);

const Dossier: React.FC<Props> = ({ children }) => {
  const { user, logged } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingDossierSummary, setLoadingDossierSummary] =
    useState<boolean>(false);
  const [loadingDossierPreview, setLoadingDossierPreview] =
    useState<boolean>(false);
  const [dossiers, setDossiers] = useState<IDossierViewModel[]>([]);

  const [orderBy, setOrder] = useState<
    IOrderFieldInputModel<Columns>[]
  >([]);
  const confirm = useAlert();
  const [selectedDownloadType, setSelectedDownloadType] = useState<
    'pdf' | 'zip'
  >('pdf');
  const [filter, setFilter] = useState<FilterProps>();

  const {
    data: results,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['dossiers'],
    ({ pageParam = 1 }) =>
      DossierController.getByUserId<Columns>({
        orderBy,
        page: pageParam,
        take: PAGE_SIZE,
        term: filter?.term,
        filter: filter?.term ? filter?.filter : undefined,
      }).then((res) => res.data),
    {
      enabled: !!user?.id && logged,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    }
  );

  const handleFetchNextPage = () => {
    fetchNextPage();
  };

  const handleFetch = () => {
    refetch();
  };

  const createDossier = async (
    input: IRequestDossierInputModel
  ): Promise<string | undefined> => {
    try {
      const response = await DossierController.requestDossier(input);
      const newDossier: IDossierViewModel = response.data;

      // Update the dossiers state by adding the new dossier
      setDossiers((prevDossiers) => [newDossier, ...prevDossiers]);

      return newDossier.id;
    } catch (error) {
      confirm.show({
        type: 'error',
        title: 'Falha ao criar dossiê',
        timeout: 5000,
        description:
          'Ocorreu um erro ao criar o dossiê. Tente novamente.',
        retry: {
          show: false,
        },
        cancel: {
          label: 'Ok',
        },
      });
      return undefined;
    }
  };

  const getDossierSummary = async (
    dossierId: string
  ): Promise<IAvailableExternalFontViewModel[] | undefined> => {
    try {
      setLoadingDossierSummary(true);
      const response = await DossierController.getDossierSummary(
        dossierId
      );
      const summary = response.data;

      setDossiers((prevDossiers) =>
        prevDossiers.map((dossier) =>
          dossier.id === dossierId
            ? { ...dossier, summaryDossier: summary }
            : dossier
        )
      );

      return summary;
    } catch (error) {
      return undefined;
    } finally {
      setLoadingDossierSummary(false);
    }
  };

  const getDossierPreview = async (
    dossierId: string
  ): Promise<IDossierPreviewViewModel | undefined> => {
    try {
      setLoadingDossierPreview(true);
      const response = await DossierController.getDossierPreview(
        dossierId
      );
      const data = response.data;
      setDossiers((prevDossiers) =>
        prevDossiers.map((dossier) =>
          dossier.id === dossierId
            ? { ...dossier, resume: data.resume }
            : dossier
        )
      );
      return data;
    } catch (error) {
      return undefined;
    } finally {
      setLoadingDossierPreview(false);
    }
  };

  useEffect(() => {
    setDossiers(results?.pages?.flat() ?? []);
  }, [results]);

  useEffect(() => {
    if (logged && user?.id) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(orderBy), JSON.stringify(filter)]);

  useWebSocket<{
    id: string;
    status: DossierStatus;
    dateToGenerateAgain: Date;
    hasFlag: boolean;
    dossierValue: number;
  }>({
    url: `dossiers/${user?.id}/update`,
    onMessage: ({
      id,
      status,
      dateToGenerateAgain,
      hasFlag,
      dossierValue,
    }) => {
      setDossiers((prev) => {
        const newDossiers = [...prev];
        const idx = newDossiers.findIndex((d) => d.id === id);
        if (idx !== -1) {
          newDossiers[idx].status = status;
          if (dateToGenerateAgain)
            newDossiers[idx].dateToGenerateAgain = new Date(
              dateToGenerateAgain
            );
          if (hasFlag) newDossiers[idx].hasFlag = hasFlag;
          newDossiers[idx].dossierValue = dossierValue;
        }
        return newDossiers;
      });
      getDossierSummary(id);
    },
  });

  useWebSocket<{ id: string; status: DossierPdfStatus }>({
    url: `dossiers/${user?.id}/request-pdf`,
    onMessage: ({ id, status }) => {
      setDossiers((prev) => {
        const newDossiers = [...prev];
        const idx = newDossiers.findIndex((d) => d.id === id);
        if (idx !== -1) newDossiers[idx].pdfStatus = status;

        if (status === DossierPdfStatus.DONE) {
          handleDownload(id, 'pdf');
        }
        return newDossiers;
      });
    },
  });

  const handleDownload = (id: string, option: 'pdf' | 'zip') => {
    setIsDownloading(true);
    switch (option) {
      case 'pdf':
        DossierController.download(id, option)
          .then((url: string) => {
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id}.${option}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          })
          .catch(() => {
            confirm.show({
              type: 'error',
              title: 'Falha ao baixar',
              timeout: 5000,
              description: `Algum erro aconteceu ao baixar o arquivo ${option.toUpperCase()}.\nTente novamente...`,
              retry: {
                show: false,
              },
              cancel: {
                label: 'Ok',
              },
            });
          })
          .finally(() => {
            setIsDownloading(false);
          });
        break;
      case 'zip':
        DossierController.download(id, 'zip')
          .then((url: string) => {
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id}.${option}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          })
          .catch(async () => {
            await confirm.show({
              type: 'error',
              title: 'Falha ao baixar',
              timeout: 5000,
              description:
                'Algum erro aconteceu ao baixar o arquivo ZIP.\nTente novamente...',
              retry: {
                show: false,
              },
              cancel: {
                label: 'Ok',
              },
            });
          })
          .finally(() => {
            setIsDownloading(false);
          });
        break;
      default:
        break;
    }
  };

  return (
    <DossierContext.Provider
      value={{
        isDownloading,
        dossiers,
        createDossier,
        setDossiers,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        orderBy,
        setOrder,
        filter,
        setFilter,
        handleFetchNextPage,
        handleFetch,
        handleDownload,
        selectedDownloadType,
        setSelectedDownloadType,
        getDossierSummary,
        loadingDossierSummary,
        getDossierPreview,
        loadingDossierPreview,
      }}
    >
      {children}
    </DossierContext.Provider>
  );
};

export const useDossier: () => DossierContextProps = () =>
  useContext(DossierContext);

export default Dossier;
